// API Validation Middleware
// Security layer for all API calls to prevent injection attacks
import { ValidationError } from '@utils/validation/datasetValidation';
import { getSecurityConfig } from '@utils/security/securityConfig';
// Debug flag for development and local environments only
const debug: boolean =
  process.env.REACT_APP_HOST_TYPE === 'dev' ||
  process.env.REACT_APP_HOST_TYPE === 'local';
// Security: Get current security configuration
const securityConfig = getSecurityConfig();
// Security: Request validation interface
interface ValidatedRequest {
  endpoint: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  data?: any;
  headers?: Record<string, string>;
}
// Security: Response validation interface matching AxiosResponse
export interface ValidatedResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: Record<string, string>;
  config: any; // Add config property to match AxiosResponse
}
// Utility: Normalize endpoint (remove trailing slashes, lowercase)
function normalizeEndpoint(endpoint: string): string {
  return endpoint.replace(/\/+$/, '');
}
// Utility: Allowlist entry type (string or RegExp)
type AllowlistEntry = string | RegExp;
// Strict allowlist with regex for parameterized routes
const ALLOWED_ENDPOINTS: AllowlistEntry[] = [
  // General
  '/info',
  // User endpoints
  '/user',
  /^\/user\/list\?[A-Za-z0-9&-=]+$/,
  // Orders endpoints
  '/orders',
  /^\/orders\/id\/[a-zA-Z0-9_-]+(?:\/documents)?$/i, // Accept any order identifier
  /^\/orders\/(list|create|update|create-checkout-session|create-checkout-session-embedded)$/,
  // Requests endpoints (Services Client)
  '/requests',
  /^\/requests\/\d+$/, // Allow /requests/{id} endpoints (GET/PATCH)
  '/requests/list',
  /^\/requests\/list\?[a-zA-Z0-9_=&-]+$/, // Allow /requests/list?ids=1&ids=2 etc.
  '/requests/order_id',
  /^\/requests\/order_id\/[^\/]+$/, // Allow /requests/order_id/{id} endpoints
  /^\/requests\?status=[^&]+$/, // Allow /requests?status={status}
  '/requests/user',
  /^\/requests\/user\/[a-zA-Z0-9_-]+$/i, // Accept any user identifier
  /^\/requests\/partner_id\/[a-zA-Z0-9_-]+$/i, // Accept any user identifier
  // Request Forms endpoints (Services Client)
  '/request_forms',
  /^\/request_forms\/\d+$/, // Allow /request_forms/{id} endpoints
  '/request_forms/request_id',
  /^\/request_forms\/request_id\/\d+\/\w+$/, // Allow /request_forms/request_id/{id}/{action} endpoints
  '/request_forms/with_file',
  /^\/request_forms\/\d+\/\w+$/,
  // Bug Reports endpoints (Core Client)
  '/bugs',
  '/bugs/submit',
  '/bugs/update',
  '/bugs/delete',
  '/bugs/add/comment',
  // Credits endpoints (Core Client)
  '/credits',
  '/credits/source',
  // Subscriptions endpoints (Core Client)
  '/subscriptions',
  '/subscriptions/source',
  // Coupons endpoints (Core Client)
  '/coupons',
  // Abstract Compounds endpoints (Abstract Compounds Client)
  '/a_compound/compounds/search',
  /^\/a_compound\/compounds\/search(\?.*)?$/, // Allow /a_compound/compounds/search with optional query params
  /^\/a_compound\/compound_details\/(\?.*)?$/, // Fixed regex pattern
  '/a_compound/compound_xrefs/search_xref',
  /^\/a_compound\/compound_xrefs\/search_xref(\?.*)?$/, // Allow /a_compound/compound_xrefs/search_xref with optional query params
  // Data Sources endpoints (DataSources Client)
  '/cayman_catalog/get_items',
  '/chemspace_catalog/get_items',
  '/gostar_catalog/get_items',
  // Help endpoints
  '/help_resources',
  /^\/help_resources\?[a-zA-Z0-9_=&]+$/i,
  // Partner endpoints
  '/partners',
  /^\/partners\?code=[^&]+$/, // Allow /partners?code={vendorCode}
  '/partners/authorizations',
  /^\/partners\/authorizations\?user_email=[^&]+$/, // Allow /partners/authorizations?user_email={email}
  /^\/partners\/[a-zA-Z0-9_-]+$/i, // Accept any partner identifier
  /^\/partners\/authorizations\/[a-zA-Z0-9_-]+$/i, // Accept any authorization identifier
  // Repository endpoints
  '/repositories',
  // TODO: Review any endpoints with parameterized or wildcard patterns for security
];
// Utility: Check if endpoint is allowed
export function isEndpointAllowed(endpoint: string): boolean {
  const normalized = normalizeEndpoint(endpoint);
  return ALLOWED_ENDPOINTS.some((allowed) => {
    if (typeof allowed === 'string') {
      return normalized === allowed;
    }
    if (allowed instanceof RegExp) {
      return allowed.test(normalized);
    }
    return false;
  });
}
// Security: Validate API endpoint (strict)
const validateEndpoint = (endpoint: string): void => {
  if (debug) console.log('🔒 SECURITY: Validating API endpoint:', endpoint);
  if (!isEndpointAllowed(endpoint)) {
    throw new ValidationError(
      `API endpoint not in allowlist: ${endpoint}`,
      'endpoint',
      'UNAUTHORIZED_ENDPOINT'
    );
  }
  // Check for suspicious patterns
  if (/\.\.|\/\/|\\|<|>|javascript:|data:|vbscript:/i.test(endpoint)) {
    throw new ValidationError(
      `API endpoint contains suspicious patterns: ${endpoint}`,
      'endpoint',
      'MALICIOUS_ENDPOINT'
    );
  }
  if (debug) console.log('✅ SECURITY: API endpoint validation passed');
};
// Security: Validate request headers
const validateHeaders = (
  headers: Record<string, string> = {}
): Record<string, string> => {
  if (debug) console.log('🔒 SECURITY: Validating request headers');
  const validatedHeaders: Record<string, string> = {};
  for (const [key, value] of Object.entries(headers)) {
    // Validate header key
    if (!/^[a-zA-Z0-9\-_]+$/.test(key) || key.length > 100) {
      throw new ValidationError(
        `Invalid header key: ${key}`,
        'headerKey',
        'INVALID_HEADER_KEY'
      );
    }
    // Validate header value
    if (typeof value !== 'string' || value.length > 10000) {
      throw new ValidationError(
        `Invalid header value for ${key}`,
        'headerValue',
        'INVALID_HEADER_VALUE'
      );
    }
    // Check for injection patterns
    if (/<script|javascript:|data:|vbscript:/i.test(value)) {
      throw new ValidationError(
        `Header value contains malicious content: ${key}`,
        'headerValue',
        'MALICIOUS_HEADER'
      );
    }
    validatedHeaders[key] = value;
  }
  if (debug) console.log('✅ SECURITY: Request headers validation passed');
  return validatedHeaders;
};
// Security: Validate request payload size
const validatePayloadSize = (data: any): void => {
  const MAX_PAYLOAD_SIZE = 100 * 1024 * 1024; // 100MB
  const jsonString = JSON.stringify(data);
  const payloadSize = new Blob([jsonString]).size;
  if (payloadSize > MAX_PAYLOAD_SIZE) {
    throw new ValidationError(
      `Request payload too large: ${payloadSize} bytes (max: ${MAX_PAYLOAD_SIZE})`,
      'payloadSize',
      'PAYLOAD_TOO_LARGE'
    );
  }
};
// Security: Sanitize response data
const sanitizeResponse = <T>(response: any): ValidatedResponse<T> => {
  if (debug) console.log('🔒 SECURITY: Sanitizing API response');
  // Basic response structure validation
  if (!response || typeof response !== 'object') {
    throw new ValidationError(
      'Invalid response structure',
      'response',
      'INVALID_RESPONSE'
    );
  }
  // Validate required response fields
  const validatedResponse: ValidatedResponse<T> = {
    data: response.data,
    status: typeof response.status === 'number' ? response.status : 500,
    statusText:
      typeof response.statusText === 'string' ? response.statusText : 'Unknown',
    headers: typeof response.headers === 'object' ? response.headers : {},
    config: response.config || {}, // Add config property
  };
  if (debug) console.log('✅ SECURITY: API response sanitization completed');
  return validatedResponse;
};
// Security: API call validation wrapper
export const validateApiCall = async <T = any>(
  request: ValidatedRequest,
  apiFunction: () => Promise<any>
): Promise<ValidatedResponse<T>> => {
  try {
    if (debug) console.log('🔒 SECURITY: Starting API call validation');
    // Validate endpoint
    validateEndpoint(request.endpoint);
    // Validate headers
    const validatedHeaders = validateHeaders(request.headers);
    // Validate payload if present
    if (request.data) {
      validatePayloadSize(request.data);
    }
    // Log security validation
    if (debug)
      console.log('🔒 SECURITY: API call validation passed for:', {
        endpoint: request.endpoint,
        method: request.method,
        hasData: !!request.data,
        headerCount: Object.keys(validatedHeaders).length,
      });
    // Execute API call
    const response = await apiFunction();
    // Sanitize and validate response
    const validatedResponse = sanitizeResponse<T>(response);
    if (debug) console.log('✅ SECURITY: API call completed successfully');
    return validatedResponse;
  } catch (error) {
    if (error instanceof ValidationError) {
      if (debug)
        console.error('🚨 SECURITY: API validation failed:', error.message);
      throw error;
    } else {
      // Check if this is a legitimate HTTP response error (axios error with response)
      if (
        error &&
        typeof error === 'object' &&
        'response' in error &&
        error.response
      ) {
        if (debug)
          console.log(
            '🔒 SECURITY: HTTP response error (not a security issue):',
            {
              status: error.response.status,
              statusText: error.response.statusText,
              endpoint: request.endpoint,
            }
          );
        // Re-throw the original axios error for proper handling by the application
        throw error;
      } else {
        if (debug)
          console.error(
            '🚨 SECURITY: Unexpected error during API call:',
            error
          );
        throw new ValidationError(
          `API call failed: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
          'apiCall',
          'API_ERROR'
        );
      }
    }
  }
};
// Security: Enhanced API client wrapper
export const createSecureApiClient = (baseClient: any) => {
  return {
    get: async (endpoint: string, config?: any) => {
      return validateApiCall(
        { endpoint, method: 'GET', headers: config?.headers },
        () => baseClient.get(endpoint, config)
      );
    },
    post: async (endpoint: string, data?: any, config?: any) => {
      return validateApiCall(
        { endpoint, method: 'POST', data, headers: config?.headers },
        () => baseClient.post(endpoint, data, config)
      );
    },
    put: async (endpoint: string, data?: any, config?: any) => {
      console.log('endpoint:', endpoint);
      console.log('data:', data);
      return validateApiCall(
        { endpoint, method: 'PUT', data, headers: config?.headers },
        () => baseClient.put(endpoint, data, config)
      );
    },
    patch: async (endpoint: string, data?: any, config?: any) => {
      return validateApiCall(
        { endpoint, method: 'PATCH', data, headers: config?.headers },
        () => baseClient.patch(endpoint, data, config)
      );
    },
    delete: async (endpoint: string, config?: any) => {
      return validateApiCall(
        { endpoint, method: 'DELETE', headers: config?.headers },
        () => baseClient.delete(endpoint, config)
      );
    },
  };
};
// Security: Rate limiting helper
export class RateLimiter {
  private requests: Map<string, number[]> = new Map();
  private readonly maxRequests: number;
  private readonly windowMs: number;
  constructor(maxRequests?: number, windowMs?: number) {
    // Use security config values or provided parameters or defaults
    this.maxRequests =
      maxRequests ?? securityConfig.rateLimit.maxRequests ?? 50;
    this.windowMs = windowMs ?? securityConfig.rateLimit.windowMs ?? 60000;
    if (debug)
      console.log('🔒 SECURITY: RateLimiter initialized with:', {
        maxRequests: this.maxRequests,
        windowMs: this.windowMs,
        windowMinutes: this.windowMs / 60000,
      });
  }
  checkLimit(identifier: string): boolean {
    const now = Date.now();
    const requests = this.requests.get(identifier) || [];
    // Remove old requests outside the window
    const validRequests = requests.filter((time) => now - time < this.windowMs);
    if (validRequests.length >= this.maxRequests) {
      if (debug)
        console.warn(`🚨 SECURITY: Rate limit exceeded for ${identifier}`);
      return false;
    }
    // Add current request
    validRequests.push(now);
    this.requests.set(identifier, validRequests);
    return true;
  }
}
// Security: Global rate limiter instance
export const globalRateLimiter = new RateLimiter(150, 60000); // 150 requests per 60,000 ms (1 minute)